import React, { useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Container } from '../style';
import { useTranslation } from 'react-i18next';
import { Search, Monitor, MapPin, Globe, User, GraduationCap, SlidersHorizontal } from 'lucide-react';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 48px 0 72px;
`;

const Hero = styled.div`
  background: #1a365d;
  border-radius: 14px;
  padding: 44px 52px;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.4s ease both;
  @media (max-width: 768px) { padding: 28px 24px; }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.7);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 10px;
`;

const HeroDesc = styled.p`
  color: rgba(255,255,255,0.55);
  font-size: 14px;
  margin: 0;
  line-height: 1.65;
`;

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.05s;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;
  position: relative;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 11px 14px 11px 40px;
  border: 1px solid #e2eaf4;
  border-radius: 9px;
  background: #fff;
  font-size: 14px;
  color: #2d3748;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &::placeholder { color: #cbd5e0; }
  &:focus { border-color: #2c5282; }
`;

const FilterWrap = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${p => p.$active ? '#2c5282' : '#e2eaf4'};
  background: ${p => p.$active ? '#2c5282' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#4a5568'};
  &:hover {
    border-color: #2c5282;
    color: ${p => p.$active ? '#fff' : '#2c5282'};
  }
`;

// ─── Stats bar ────────────────────────────────────────────────────────────────

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.08s;
`;

const StatsCount = styled.p`
  font-size: 13px;
  color: #718096;
  margin: 0;
  font-weight: 500;
`;

// ─── Events grid ──────────────────────────────────────────────────────────────

const EventGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.1s;
`;

const EventCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2eaf4;
  padding: 20px 24px;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  transition: box-shadow 0.2s, border-color 0.2s;
  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.07);
    border-color: #bee3f8;
  }
  @media (max-width: 640px) { flex-direction: column; gap: 14px; }
`;

const EventIndex = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: #f0f4ff;
  color: #2c5282;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const EventBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const EventTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1a365d;
  margin: 0 0 12px;
  line-height: 1.5;
`;

const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MetaChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${p => p.$bg || '#f7f9fc'};
  color: ${p => p.$color || '#4a5568'};
  border: 1px solid ${p => p.$border || '#e8eef7'};
`;

const ModeBadge = styled(MetaChip)`
  background: ${p => p.$online ? '#e6fffa' : '#ebf4ff'};
  color: ${p => p.$online ? '#276749' : '#2c5282'};
  border-color: ${p => p.$online ? '#9ae6b4' : '#bee3f8'};
`;

// ─── Empty state ──────────────────────────────────────────────────────────────

const Empty = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2eaf4;
  color: #a0aec0;
  font-size: 14px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const EduCenter = () => {
  const { t, i18n } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [query, setQuery] = useState('');

  const rawEvents = t('events', { returnObjects: true });
  const events = Array.isArray(rawEvents) ? rawEvents : [];

  const filters = t('filters', { returnObjects: true });
  const filterEntries = filters && typeof filters === 'object' ? Object.entries(filters) : [];

  const filtered = useMemo(() => {
    return events.filter(ev => {
      const matchFilter = activeFilter === 'all' || ev.mode === activeFilter || ev.language === activeFilter;
      const matchQuery = !query || ev.title.toLowerCase().includes(query.toLowerCase());
      return matchFilter && matchQuery;
    });
  }, [events, activeFilter, query]);

  return (
    <PageWrapper>
      <Container>

        <Hero>
          <HeroBadge>
            <GraduationCap size={11} />
            {t('eduCenter.badge', 'Учебный центр')}
          </HeroBadge>
          <HeroTitle>{t('eduCenter.title', 'Учебный план АБТ')}</HeroTitle>
          <HeroDesc>{t('eduCenter.description', 'Мероприятия, семинары и тренинги Ассоциации банков Таджикистана')}</HeroDesc>
        </Hero>

        <Toolbar>
          <SearchWrap>
            <SearchIcon size={15} />
            <SearchInput
              type="text"
              placeholder={t('search.placeholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </SearchWrap>
          <FilterWrap>
            {filterEntries.map(([key, value]) => (
              <FilterBtn
                key={key}
                $active={activeFilter === key}
                onClick={() => setActiveFilter(key)}
              >
                {value}
              </FilterBtn>
            ))}
          </FilterWrap>
        </Toolbar>

        <StatsBar>
          <StatsCount>
            {t('eduCenter.showing', 'Показано')}: {filtered.length} / {events.length}
          </StatsCount>
        </StatsBar>

        <EventGrid>
          {filtered.length === 0 ? (
            <Empty>{t('eduCenter.empty', 'Ничего не найдено')}</Empty>
          ) : (
            filtered.map((event, index) => (
              <EventCard key={index}>
                <EventIndex>{index + 1}</EventIndex>
                <EventBody>
                  <EventTitle>{event.title}</EventTitle>
                  <EventMeta>

                    <MetaChip $bg="#f7f9fc" $color="#4a5568" $border="#e8eef7">
                      <SlidersHorizontal size={11} />
                      {event.month}
                    </MetaChip>

                    <ModeBadge $online={event.mode === 'online'}>
                      {event.mode === 'online'
                        ? <Monitor size={11} />
                        : <MapPin size={11} />
                      }
                      {t(`trainingItem.modes.${event.mode}`)}
                    </ModeBadge>

                    <MetaChip $bg="#fef9ec" $color="#92400e" $border="#fde68a">
                      <Globe size={11} />
                      {t(`trainingItem.languages.${event.language}`)}
                    </MetaChip>

                    {event.responsible && (
                      <MetaChip $bg="#fdf2f8" $color="#97266d" $border="#fbcfe8">
                        <User size={11} />
                        {event.responsible}
                      </MetaChip>
                    )}

                  </EventMeta>
                </EventBody>
              </EventCard>
            ))
          )}
        </EventGrid>

      </Container>
    </PageWrapper>
  );
};

export default EduCenter;